import React, { useState } from 'react';
import { History, Trash2, Calendar, ExternalLink, CheckCircle2, Copy, Zap, Link2 } from 'lucide-react';
import { HistoryItem } from '@/types';
import { formatDate, openLink } from '@/utils';

interface HistoryListProps {
    history: HistoryItem[];
    onClear: () => void;
    isMobile: boolean;
    isLoggedIn: boolean;
}

export const HistoryList: React.FC<HistoryListProps> = ({ history, onClear, isMobile, isLoggedIn }) => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div className="glass-card rounded-3xl border border-white/50 dark:border-gray-700/50 shadow-2xl shadow-blue-900/10 dark:shadow-black/50 overflow-hidden animate-in slide-in-from-right-8 duration-500 min-h-[460px] flex flex-col  max-w-4xl mx-auto">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex flex-col gap-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md">
                <div className="flex items-center justify-center gap-2">
                    <h3 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center">
                        <History className="w-5 h-5 text-blue-500" />
                        Atividades Recentes
                    </h3>
                    {history.length > 0 && !isLoggedIn && (
                        <button
                            onClick={onClear}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title="Limpar histórico"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
                {!isLoggedIn && (
                    <p className="text-[10px] text-orange-600 dark:text-orange-400 font-medium bg-orange-100 dark:bg-orange-900/20 px-2 py-1 rounded self-center">
                        ⚠️ Histórico salvo apenas neste dispositivo
                    </p>
                )}
            </div>

            <div className="flex-1 overflow-y-auto max-h-[460px] p-4 space-y-3 no-scrollbar">
                {history.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 py-20 text-center">
                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                            <History className="w-8 h-8 opacity-30" />
                        </div>
                        <p className="text-sm font-medium">Nenhum link otimizado ainda.</p>
                        <p className="text-xs opacity-60 mt-1">Seus links recentes aparecerão aqui.</p>
                    </div>
                ) : (
                    history.map((item, idx) => (
                        <div
                            key={item.id}
                            className="group relative bg-white dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 rounded-2xl p-4 transition-all hover:bg-blue-50/50 dark:hover:bg-blue-900/10 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-lg animate-in slide-in-from-bottom-2"
                            style={{ animationDelay: `${idx * 50}ms` }}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded shadow-sm text-white ${item.platform === 'shopee'
                                        ? 'bg-gradient-to-r from-orange-400 to-red-400'
                                        : item.platform === 'amazon'
                                            ? 'bg-gradient-to-r from-gray-700 to-black'
                                            : 'bg-gradient-to-r from-yellow-400 to-orange-400'
                                        }`}>
                                        {item.platform === 'mercadolivre' ? 'ML' : item.platform === 'shopee' ? 'SHOPEE' : 'AMZ'}
                                    </span>
                                    <span className="text-[10px] text-gray-400 flex items-center gap-1 font-medium">
                                        <Calendar className="w-3 h-3" /> {formatDate(item.date)}
                                    </span>
                                </div>

                                <div className="flex gap-2">
                                    {isMobile && (
                                        <button
                                            onClick={() => openLink(item.shortUrl)}
                                            className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleCopy(item.shortUrl, item.id)}
                                        className={`p-2 rounded-xl transition-all ${copiedId === item.id
                                            ? 'bg-green-500 text-white shadow-lg shadow-green-500/20'
                                            : 'bg-white dark:bg-gray-700 text-gray-500 hover:text-blue-600 shadow-sm hover:shadow-md'
                                            }`}
                                    >
                                        {copiedId === item.id ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2 bg-white/60 dark:bg-black/20 p-2.5 rounded-xl border border-gray-100 dark:border-white/5">
                                    <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600">
                                        <Zap className="w-3 h-3" />
                                    </div>
                                    <a href={item.shortUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-blue-600 dark:text-blue-400 truncate flex-1">
                                        {item.shortUrl}
                                    </a>
                                </div>
                                <div className="flex items-center gap-1.5 px-1">
                                    <Link2 className="w-3 h-3 text-gray-400" />
                                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate flex-1 opacity-70 group-hover:opacity-100 transition-opacity">
                                        {item.originalUrl}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
